import React, { useCallback, useEffect, useRef, useState } from "react";
import { ensureMapbox, getMapboxToken } from "../utils/mapboxToken";

export default function ModalConfirmacion({
  abierto,
  onCerrar,
  ubicacionCliente,
  onConfirmarDomicilio,
}) {
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [referencias, setReferencias] = useState("");
  const [comentarios, setComentarios] = useState("");
  const [direccion, setDireccion] = useState("");
  const [sugerencias, setSugerencias] = useState([]);

  const mapRef = useRef(null);
  const mapboxMapRef = useRef(null);
  const markerRef = useRef(null);
  const debounceRef = useRef(null);
  const coordsEntregaRef = useRef(null);
  const destinoElegidoRef = useRef(false);

  const tokenMapbox = getMapboxToken();
  const [mapboxListo, setMapboxListo] = useState(false);
  const [mapboxFallo, setMapboxFallo] = useState(false);
  const [puntoInicialMapa, setPuntoInicialMapa] = useState(null);
  const mapaInteractivo = mapboxListo && Boolean(tokenMapbox);

  const reverseGeocode = useCallback(
    async (lat, lng, opciones = {}) => {
      if (!tokenMapbox) return;
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?language=es&types=address,place&limit=1&access_token=${tokenMapbox}`
        );
        const data = await res.json();
        if (opciones.soloSiNoElegido && destinoElegidoRef.current) {
          return;
        }
        if (data.features?.[0]) {
          setDireccion(data.features[0].place_name);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [tokenMapbox]
  );

  useEffect(() => {
    if (!abierto) {
      return;
    }
    if (!tokenMapbox) {
      setMapboxListo(false);
      setMapboxFallo(false);
      return;
    }

    let cancelado = false;
    ensureMapbox().then((ok) => {
      if (cancelado) return;
      setMapboxListo(ok);
      setMapboxFallo(!ok);
    });

    return () => {
      cancelado = true;
    };
  }, [abierto, tokenMapbox]);

  useEffect(() => {
    if (!abierto) {
      setPuntoInicialMapa(null);
      return;
    }
    if (puntoInicialMapa) return;
    if (destinoElegidoRef.current && coordsEntregaRef.current) {
      setPuntoInicialMapa(coordsEntregaRef.current);
      return;
    }
    if (ubicacionCliente?.lat != null && ubicacionCliente?.lon != null) {
      const coords = { lat: ubicacionCliente.lat, lon: ubicacionCliente.lon };
      if (!coordsEntregaRef.current) {
        coordsEntregaRef.current = coords;
      }
      setPuntoInicialMapa(coords);
    }
  }, [abierto, ubicacionCliente, puntoInicialMapa]);

  /* ================= INIT MAP ================= */

  useEffect(() => {
    if (!abierto || !mapaInteractivo || !puntoInicialMapa) return;
    if (!mapRef.current) return;

    window.mapboxgl.accessToken = tokenMapbox;

    const center = [puntoInicialMapa.lon, puntoInicialMapa.lat];

    const map = new window.mapboxgl.Map({
      container: mapRef.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center,
      zoom: 16,
    });

    const marker = new window.mapboxgl.Marker({ draggable: true })
      .setLngLat(center)
      .addTo(map);

    markerRef.current = marker;
    mapboxMapRef.current = map;

    marker.on("dragend", async () => {
      const pos = marker.getLngLat();
      coordsEntregaRef.current = { lat: pos.lat, lon: pos.lng };
      destinoElegidoRef.current = true;
      map.easeTo({ center: [pos.lng, pos.lat] });
      await reverseGeocode(pos.lat, pos.lng);
    });

    map.on("click", async (e) => {
      marker.setLngLat(e.lngLat);
      coordsEntregaRef.current = { lat: e.lngLat.lat, lon: e.lngLat.lng };
      destinoElegidoRef.current = true;
      await reverseGeocode(e.lngLat.lat, e.lngLat.lng);
    });

    if (!destinoElegidoRef.current) {
      reverseGeocode(puntoInicialMapa.lat, puntoInicialMapa.lon, {
        soloSiNoElegido: true,
      });
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      markerRef.current = null;
      if (mapboxMapRef.current) {
        mapboxMapRef.current.remove();
        mapboxMapRef.current = null;
      }
    };
  }, [abierto, puntoInicialMapa, mapaInteractivo, tokenMapbox, reverseGeocode]);

  async function buscarDireccion(texto) {
    const q = texto.trim();
    if (!q) {
      setSugerencias([]);
      return;
    }
    if (!tokenMapbox) {
      setSugerencias([]);
      return;
    }
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          q
        )}.json?country=cl&language=es&autocomplete=true&limit=5&access_token=${tokenMapbox}`
      );
      const data = await res.json();
      setSugerencias(data.features || []);
    } catch (e) {
      console.error(e);
      setSugerencias([]);
    }
  }

  function manejarCambioDireccion(valor) {
    setDireccion(valor);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      buscarDireccion(valor);
    }, 300);
  }

  function seleccionarSugerencia(feature) {
    const [lng, lat] = feature.center || [];
    const coords =
      Number.isFinite(lat) && Number.isFinite(lng)
        ? { lat, lon: lng }
        : null;

    setDireccion(feature.place_name || "");
    setSugerencias([]);
    destinoElegidoRef.current = true;
    if (coords) {
      coordsEntregaRef.current = coords;
    }

    if (mapboxMapRef.current && markerRef.current && coords) {
      markerRef.current.setLngLat([lng, lat]);
      mapboxMapRef.current.easeTo({ center: [lng, lat], zoom: 16 });
      return;
    }

    if (coords) {
      setPuntoInicialMapa(coords);
    }
  }

  function obtenerCoordenadasEntrega() {
    if (markerRef.current) {
      const pos = markerRef.current.getLngLat();
      return { lat: pos.lat, lon: pos.lng };
    }
    if (coordsEntregaRef.current) {
      return coordsEntregaRef.current;
    }
    if (ubicacionCliente?.lat != null && ubicacionCliente?.lon != null) {
      return { lat: ubicacionCliente.lat, lon: ubicacionCliente.lon };
    }
    return { lat: null, lon: null };
  }

  /* ================= CONFIRM ================= */

  function confirmar() {
    if (!nombre.trim()) return alert("Ingresa tu nombre");
    if (!telefono.trim()) return alert("Ingresa tu teléfono");
    if (!direccion.trim()) return alert("Ingresa tu dirección de entrega");

    const coords = obtenerCoordenadasEntrega();

    const data = {
      canal: "delivery",
      cliente: {
        nombre: nombre.trim(),
        telefono: telefono.trim(),
      },
      direccion: {
        texto: direccion.trim(),
        lat: coords.lat,
        lon: coords.lon,
        referencias: referencias.trim() || null,
        comentarios: comentarios.trim() || null,
      },
    };

    onConfirmarDomicilio(data);
  }

  if (!abierto) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", background: "rgba(0,0,0,0.5)", zIndex: 1050 }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content rounded-4">

          <div className="modal-header">
            <h5 className="modal-title fw-bold">
              Dirección de entrega
            </h5>
            <button className="btn-close" onClick={onCerrar}></button>
          </div>

          <div className="modal-body">

            <label className="fw-semibold">Nombre</label>
            <input
              className="form-control"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Tu nombre"
            />

            <label className="fw-semibold mt-3">Teléfono</label>
            <input
              className="form-control"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="+56 9 ..."
            />

            <label className="fw-semibold mt-3">Buscar dirección</label>
            <input
              className="form-control"
              placeholder="Calle, número, comuna..."
              value={direccion}
              onChange={(e) => manejarCambioDireccion(e.target.value)}
            />
            {sugerencias.length > 0 && (
              <div className="list-group mt-2">
                {sugerencias.map((feature) => (
                  <button
                    type="button"
                    key={feature.id}
                    className="list-group-item list-group-item-action"
                    onClick={() => seleccionarSugerencia(feature)}
                  >
                    {feature.place_name}
                  </button>
                ))}
              </div>
            )}

            {tokenMapbox && !mapboxFallo ? (
              <div
                ref={mapRef}
                style={{
                  height: 320,
                  width: "100%",
                  marginTop: 15,
                  borderRadius: 12,
                }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center text-muted small text-center px-3"
                style={{
                  height: 320,
                  width: "100%",
                  marginTop: 15,
                  borderRadius: 12,
                  backgroundColor: "#f3f3f3",
                }}
              >
                {tokenMapbox
                  ? "No se pudo cargar el mapa. Puedes escribir tu dirección manualmente."
                  : "Mapa no configurado en este despliegue. Escribe tu dirección manualmente."}
              </div>
            )}

            <small className="text-muted d-block mt-2">
              Mueve el marcador si deseas ajustar el punto exacto
            </small>

            <label className="fw-semibold mt-3">Referencias</label>
            <textarea
              className="form-control"
              rows="2"
              value={referencias}
              onChange={(e) => setReferencias(e.target.value)}
              placeholder="Portón negro, casa esquina, etc."
            />

            <label className="fw-semibold mt-3">Comentarios</label>
            <textarea
              className="form-control"
              rows="2"
              value={comentarios}
              onChange={(e) => setComentarios(e.target.value)}
              placeholder="Indicaciones extras"
            />
          </div>

          <div className="modal-footer">
            <button className="btn btn-danger" onClick={onCerrar}>
              Cancelar
            </button>
            <button className="btn btn-dark fw-bold" onClick={confirmar}>
              Confirmar pedido
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
